import { Reveal } from "@/lib/reveal";
import HeroVideo from "@/components/HeroVideo";
import ServiceCard from "@/components/ServiceCard";
import Signature from "@/components/Signature";
import AmbientVideo from "@/components/AmbientVideo";
import { AMBIENT_VIDEOS, AMBIENT_POSTERS } from "@/data/media";
import { usePageMeta } from "@/lib/usePageMeta";
import { SERVICES } from "@/data/services";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, HeartHandshake, Compass, MessageCircle, Coffee, Footprints, PhoneCall, Heart, Sunrise, Waves, Users, Leaf } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FOUNDER } from "@/data/founder";

const SECONDARY_IMG = "https://images.pexels.com/photos/32510373/pexels-photo-32510373.png";

const WHO_I_SUPPORT = [
  {
    icon: Sunrise,
    title: "Anyone in a transition",
    body:
      "A new chapter, an empty house, a job change, a loss, a beginning — moments where life just feels different and you need someone steady to talk it through.",
  },
  {
    icon: Waves,
    title: "Anyone feeling overwhelmed",
    body:
      "The kind of overwhelm that doesn't have a single name — just a lot, all at once. A calm conversation can help you breathe again.",
  },
  {
    icon: Users,
    title: "Caregivers & quiet givers",
    body:
      "If you spend your days holding space for everyone else, this is a space made for you. You're allowed to be the one who's heard.",
  },
  {
    icon: Leaf,
    title: "Anyone who just wants company",
    body:
      "No big crisis required. Sometimes the most healing thing is simply not being alone for an hour. That's enough of a reason.",
  },
];


const PILLARS = [
  {
    icon: HeartHandshake,
    title: "Presence over advice",
    body:
      "Sometimes you don't need a fix. You need someone steady to sit with you while you find your own way through.",
  },
  {
    icon: Compass,
    title: "Lived experience, not labels",
    body:
      "This is support shaped by real life — by transitions, quiet wins, and the long roads in between.",
  },
  {
    icon: Sparkles,
    title: "Soft, never pushy",
    body:
      "No scripts, no pressure. You share as much or as little as feels right, and we move at your pace.",
  },
];

const MOODS = [
  { icon: MessageCircle, label: "I just need to talk", note: "Talking" },
  { icon: Coffee, label: "Coffee & Conversation", note: "Coffee & Conversation" },
  { icon: Footprints, label: "Walking Support Session", note: "Walking Support Session" },
  { icon: PhoneCall, label: "A quick check-in call", note: "Check-in call" },
  { icon: HeartHandshake, label: "I'm not sure — help me decide", note: "Not sure yet" },
];

function ConnectionSpace() {
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  const onPick = (idx) => {
    setActive(idx);
    setTimeout(() => {
      navigate(`/contact?intent=${encodeURIComponent(MOODS[idx].note)}`);
    }, 650);
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {MOODS.map((m, i) => {
        const Icon = m.icon;
        const isActive = active === i;
        return (
          <button
            key={m.label}
            data-testid={`mood-chip-${i}`}
            onClick={() => onPick(i)}
            className={`group relative text-left flex items-center gap-4 p-5 rounded-2xl border transition-all duration-500 overflow-hidden ${
              isActive
                ? "bg-[var(--cc-primary)] border-[var(--cc-primary)] text-white scale-[1.02]"
                : "bg-white border-[var(--cc-line)] hover:border-[var(--cc-primary)]/40 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(0,0,0,0.05)]"
            }`}
          >
            <span
              className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-colors duration-500 ${
                isActive ? "bg-white/15" : "bg-[var(--cc-surface)] group-hover:bg-[var(--cc-primary)]/10"
              }`}
            >
              <Icon
                size={18}
                strokeWidth={1.5}
                className={isActive ? "text-white" : "text-[var(--cc-primary)]"}
              />
            </span>
            <span className="flex-1 font-serif text-lg md:text-xl leading-snug">{m.label}</span>
            <ArrowRight
              size={16}
              strokeWidth={1.7}
              className={`transition-transform duration-500 ${
                isActive ? "translate-x-1 text-white" : "text-[var(--cc-text-soft)] group-hover:translate-x-1"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function HomePage() {
  usePageMeta({
    title: "Calm & Connected — One-on-One Peer Support & Companionship",
    description:
      "A warm, judgment-free space for connection, reflection, and personal growth. One-on-one peer support sessions by phone, video, or in person — rooted in lived experience.",
  });
  return (
    <div data-testid="home-page">
      <HeroVideo />

      {/* WELCOME */}
      <section className="cc-section relative overflow-hidden">
        <span aria-hidden className="cc-orb cc-orb-1" style={{opacity: 0.35}} />
        <div className="cc-container relative">
          <div className="max-w-3xl">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                Welcome
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-tight">
                A space for real support — <em className="italic">without pressure or judgment.</em>
              </h2>
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-12 gap-10 md:gap-14 items-start">
            <Reveal delay={120} className="md:col-span-7 md:order-1 order-2">
              <p className="text-lg text-[var(--cc-text-soft)] leading-relaxed">
                If you&apos;ve been holding a lot in, feeling overwhelmed, or
                just need someone to truly hear you — you&apos;re not alone.
                Calm &amp; Connected is a warm, one-on-one space to talk
                something through, get out of your head, or simply not feel
                alone for a moment.
              </p>
              <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
                These sessions are about being heard. About slowing down. About
                feeling grounded, supported, and a little more clear by the end
                of our time together.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/services" data-testid="welcome-cta-services" className="cc-btn-primary">
                  Explore Support Options <ArrowRight size={16} strokeWidth={1.7} />
                </Link>
                <Link to="/about" data-testid="welcome-cta-about" className="cc-btn-secondary">
                  About Calm &amp; Connected
                </Link>
              </div>
              <div className="mt-10">
                <Signature prefix="with care," size="md" testId="home-signature" />
              </div>
            </Reveal>

            {/* Visual stack — calming ambient video card */}
            <Reveal delay={240} className="md:col-span-5 md:order-2 order-1">
              <div className="relative">
                <AmbientVideo
                  src={AMBIENT_VIDEOS.landscape}
                  poster={AMBIENT_POSTERS.landscape}
                  overlay="light"
                  className="w-full h-[320px] md:h-[460px] cc-card"
                  testId="welcome-ambient-video"
                />
                <div className="hidden md:block absolute -bottom-6 -left-6 w-48 cc-card p-5 bg-white">
                  <p className="font-serif italic text-lg leading-snug text-[var(--cc-text)]">
                    &ldquo;You are allowed to slow down here.&rdquo;
                  </p>
                </div>
                <div className="hidden md:block absolute -top-8 -right-8 w-28 h-28 rounded-full bg-[var(--cc-secondary)]/35 blur-2xl" />
                <div className="hidden md:block absolute -bottom-12 -right-4 w-24 h-24 rounded-full bg-[var(--cc-primary)]/15 blur-2xl" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO I SUPPORT */}
      <section
        data-testid="home-who-i-support"
        className="cc-section relative bg-[var(--cc-surface)] overflow-hidden"
      >
        <span aria-hidden className="cc-orb cc-orb-2" style={{opacity: 0.25}} />
        <div className="cc-container relative">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                Who I support
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                If you&apos;ve been carrying a lot — this space is <em className="italic">for you.</em>
              </h2>
              <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
                You don&apos;t need a diagnosis, a crisis, or the right words.
                If any of these feel a little like you, you&apos;re welcome here.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
            {WHO_I_SUPPORT.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={i * 110}>
                  <div className="cc-card p-7 md:p-8 h-full flex items-start gap-5 transition-transform duration-500 hover:-translate-y-1">
                    <span className="w-12 h-12 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center shrink-0">
                      <Icon size={18} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                    </span>
                    <div>
                      <h3 className="font-serif text-2xl leading-snug">{w.title}</h3>
                      <p className="mt-2 text-[var(--cc-text-soft)] leading-relaxed">
                        {w.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={120}>
            <p className="mt-10 text-center text-[var(--cc-text-soft)] italic">
              And of course — anyone who simply wants to be met where they are.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOW I CAN SUPPORT YOU */}
      <section
        data-testid="home-services-section"
        className="cc-section relative"
      >
        <div className="cc-container">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                How I can support you
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                Three ways to feel a little more held this week.
              </h2>
              <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
                Each session is one-on-one and shaped around what would feel
                most helpful for you. Not sure which one fits? Reach out and
                we'll figure it out together.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 120}>
                <ServiceCard service={s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CALM & CONNECTED — features Camili's real photo */}
      <section className="cc-section relative overflow-hidden bg-[var(--cc-surface)]">
        <span aria-hidden className="cc-orb cc-orb-2" style={{opacity: 0.3}} />
        <div className="cc-container grid md:grid-cols-12 gap-14 items-center relative">
          <Reveal className="md:col-span-6">
            <div className="relative">
              <div className="rounded-[36px] overflow-hidden cc-card p-0 bg-[var(--cc-surface)]">
                <img
                  src={FOUNDER.portraitPrimary}
                  alt={`${FOUNDER.name}, founder of Calm & Connected`}
                  loading="eager"
                  decoding="async"
                  className="w-full h-[520px] md:h-[600px] object-contain"
                />
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 px-5 py-3 rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.06)] border border-[var(--cc-line)]">
                <p className="font-serif text-lg leading-tight text-[var(--cc-text)]">
                  Hi, I&apos;m {FOUNDER.name}
                </p>
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--cc-text-soft)] mt-1">
                  Founder · Calm &amp; Connected
                </p>
              </div>
              <div className="hidden md:block absolute -top-8 -right-8 w-36 h-36 rounded-full bg-[var(--cc-primary)]/15 blur-2xl" />
            </div>
          </Reveal>

          <div className="md:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                Why Calm &amp; Connected
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                Built on presence,<br />
                <em className="italic">shaped by lived experience.</em>
              </h2>
              <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
                I&apos;m {FOUNDER.name} — a mother, grandmother, and caregiver
                who has navigated my own seasons of grief, change, and quiet
                healing. Calm &amp; Connected is what I wish I&apos;d had on
                the hardest days: a steady person, no agenda, just real warmth.
              </p>
            </Reveal>
            <div className="mt-10 space-y-7">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={i * 120}>
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center shrink-0">
                      <p.icon size={18} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl leading-snug">{p.title}</h3>
                      <p className="mt-2 text-[var(--cc-text-soft)] leading-relaxed">
                        {p.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={200}>
              <Link
                to="/about"
                data-testid="why-cta-about"
                className="cc-btn-secondary mt-10 inline-flex"
              >
                Read my story <ArrowRight size={16} strokeWidth={1.7} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONNECTION SPACE — interactive mood picker */}
      <section
        data-testid="connection-space"
        className="cc-section relative bg-[var(--cc-surface)] overflow-hidden"
      >
        <span aria-hidden className="cc-orb cc-orb-1" />
        <span aria-hidden className="cc-orb cc-orb-2" />
        <div className="cc-container grid md:grid-cols-12 gap-12 items-start relative">
          <Reveal className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              The Connection Space
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
              How would you like to <em className="italic">start?</em>
            </h2>
            <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
              No long forms. No labels. Pick whatever feels closest to where you
              are today — even &quot;not sure&quot; is a perfectly good answer.
              I&apos;ll meet you there.
            </p>
            <div className="mt-8 flex items-center gap-3 text-sm text-[var(--cc-text-soft)]">
              <span className="cc-pulse-dot" />
              I usually reply the same day.
            </div>
          </Reveal>

          <Reveal delay={140} className="md:col-span-7">
            <ConnectionSpace />
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section
        data-testid="home-cta-band"
        className="relative cc-section overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${SECONDARY_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[var(--cc-bg)]/70" />
        <div className="relative cc-container text-center max-w-3xl mx-auto">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              Let's talk — just start where you are
            </p>
            <h2 className="mt-5 font-serif text-4xl md:text-6xl leading-tight">
              You can share as much or as little as you'd like.
            </h2>
            <p className="mt-6 text-lg text-[var(--cc-text-soft)] leading-relaxed">
              No pressure. No judgment. Just support — meeting you exactly where you are.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/booking" data-testid="cta-band-primary" className="cc-btn-primary">
                Connect With Me <ArrowRight size={16} strokeWidth={1.7} />
              </Link>
              <Link to="/contact" data-testid="cta-band-secondary" className="cc-btn-secondary">
                Send a Note
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
